import api from "./api";

// Voice-command action dispatcher. Receives a structured intent from
// the backend `/api/voice/intent` endpoint and executes it against
// React Router + the global player event bus.
//
// Intent envelope:
//   { action: "navigate" | "play" | "read" | "control" | "search" | "unknown",
//     target: string,
//     params?: object,
//     say?: string }
//
// Why cross-page control needs an event bus: the player modals
// (AudioVisualizerPlayer, InAppVideoPlayer) are mounted inside the
// individual page components. The voice commander can be invoked from
// any page, so we broadcast a CustomEvent that any open modal can
// listen for. Modals dispatch their own internal handlers from there.

export const VOICE_BUS_EVENT = "hw-voice-command";

// Hard-stop EVERY piece of media on the page — native <video>/<audio>,
// YouTube iframes (via the IFrame API postMessage), the mini-player and
// the TTS AudioBar (via the hw:stop-all-media broadcast). Fired right
// before a voice command starts NEW playback so the old video never
// keeps playing underneath the new one.
export function stopAllMedia() {
  if (typeof window === "undefined") return;
  try {
    document.querySelectorAll("video, audio").forEach((el) => {
      try { el.pause(); } catch (_) {}
    });
  } catch (_) {}
  try {
    document.querySelectorAll("iframe").forEach((f) => {
      if ((f.src || "").includes("youtube.com")) {
        try { f.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', "*"); } catch (_) {}
      }
    });
  } catch (_) {}
  try { window.dispatchEvent(new CustomEvent("hw:stop-all-media")); } catch (_) {}
}

export function dispatchVoiceControl(verb) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(VOICE_BUS_EVENT, { detail: { verb } }));
}

// Speak a short confirmation. As of Feb 2026 this routes through the
// HD TTS endpoint (/api/tts/speak) using the user's currently selected
// voice profile (Cartoon Mouse, Civil Rights Preacher, Church Grandma,
// etc.) so the Voice Commander's "Got it" / "I couldn't find that"
// confirmations match the character voice the user picked in Settings.
//
// When no HD voice is configured we still fall back to the browser's
// built-in speechSynthesis so the feature keeps working for users who
// haven't opened Settings yet — and so we don't burn LLM-key tokens on
// users who haven't asked for the upgrade.
export function voiceSpeak(text) {
  if (!text || typeof window === "undefined") return;
  // Tell every other speech source in the app to hard-stop so the
  // confirmation doesn't overlap with Bible read-aloud or any sermon
  // TTS that's already running.
  try { window.dispatchEvent(new CustomEvent("hw:speech:external-start")); } catch (_) {}
  try { window.dispatchEvent(new CustomEvent("hw:read-aloud:start", { detail: { id: "voice-commander", label: "Voice assistant" } })); } catch (_) {}

  // Read the active HD profile from localStorage. We import lazily so
  // SSR-safe paths above still work even if window isn't ready.
  let hdVoice = null;
  try { hdVoice = window.localStorage.getItem("hw:audio:hdVoice"); } catch (_) {}

  if (hdVoice) {
    // Cut any in-flight singleton audio IMMEDIATELY — before the fetch
    // round-trip to /api/tts/speak. Without this the user keeps hearing
    // the previous clip for ~300-800ms while the new TTS blob downloads,
    // so the two voices overlap when the new clip finally starts.
    try { import("./singleAudio").then(({ stopOnce }) => stopOnce()); } catch (_) {}
    // Route through the singleton + /api/tts/speak so the confirmation
    // uses the user's chosen character voice. Singleton stops any
    // other clip first — no overlap.
    Promise.all([
      import("./singleAudio"),
      import("./api"),
    ]).then(async ([{ playOnce }, { default: api }]) => {
      try {
        const resp = await api.post(
          "/tts/speak",
          { text, voice: hdVoice, model: "tts-1", speed: 1.05 },
          { responseType: "blob" }
        );
        const url = URL.createObjectURL(resp.data);
        await playOnce(url);
        // Revoke the blob once playback finishes — keeps memory tidy
        // even if the user fires off lots of voice commands.
        setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_) {} }, 30000);
      } catch (_) {
        // If the HD path fails for any reason (auth, network, server),
        // gracefully fall back to the device voice so the user still
        // hears the confirmation.
        try {
          if (window.speechSynthesis) {
            window.speechSynthesis.cancel();
            const u = new SpeechSynthesisUtterance(text);
            u.rate = 1.05;
            window.speechSynthesis.speak(u);
          }
        } catch (_) {}
      }
    }).catch(() => {});
    return;
  }

  // No HD voice selected — keep the original behavior.
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.05;
    u.pitch = 1.0;
    u.volume = 1.0;
    window.speechSynthesis.speak(u);
  } catch (_) { /* ignore */ }
}

/**
 * Execute a parsed intent. Returns true on success.
 *
 * `navigate({to, replace})` should be a function from useNavigate().
 * The caller passes its own so this lib stays React-Router-version
 * agnostic and testable.
 */
export async function executeVoiceIntent(intent, { navigate, queryClient } = {}) {
  if (!intent || !intent.action) return false;
  const { action, target, params, say } = intent;
  if (say) voiceSpeak(say);

  switch (action) {
    case "navigate":
      if (target && navigate) navigate(target);
      return true;
    case "read":
      // target already includes /read/<book> or /apocrypha/<book>[/chapter]
      stopAllMedia();
      if (target && navigate) navigate(target);
      return true;
    case "control":
      dispatchVoiceControl(target);
      return true;
    case "play":
      stopAllMedia();
      return await executeVoicePlay(intent, { navigate });
    case "search":
      if (navigate) navigate(`/search?q=${encodeURIComponent(params?.query || target || "")}`);
      return true;
    default:
      return false;
  }
}

// Map intent.target buckets → catalog endpoints + landing routes. The
// landing route receives `?play=<id>` so the existing MediaGrid /
// VideoMediaGrid auto-opens the player on that track.
const BUCKET_ROUTES = {
  christian_rap:  { endpoint: "/christian-rap",   route: "/christian-rap" },
  gospel_music:   { endpoint: "/gospel-music",    route: "/gospel-music" },
  music_videos:   { endpoint: "/music-videos",    route: "/music-videos" },
  sermons:        { endpoint: "/sermons",         route: "/sermons" },
  lessons:        { endpoint: "/lessons",         route: "/lessons" },
  videos:         { endpoint: "/videos",          route: "/videos" },
  choir:          { endpoint: "/choir",           route: "/choir" },
  movies:         { endpoint: "/movies",          route: "/movies" },
};

async function executeVoicePlay(intent, { navigate }) {
  const query = (intent.params?.query || "").toLowerCase().trim();
  if (!query) {
    if (navigate) navigate("/gospel-music");
    return true;
  }
  // Decide which catalogs to search. If the regex picked a bucket use
  // that one; otherwise sweep the four most-played buckets.
  const buckets = intent.target && intent.target !== "auto"
    ? [intent.target]
    : ["christian_rap", "gospel_music", "music_videos", "sermons", "videos", "movies", "choir", "lessons"];

  // Generic bucket requests — "play gospel music", "play sermons" —
  // shouldn't be fuzzy-matched against track titles (that dead-ends in
  // search). Jump straight to the bucket and auto-play its first track.
  const GENERIC_QUERIES = {
    gospel_music: ["gospel music", "gospel", "worship", "worship music", "praise music", "praise"],
    christian_rap: ["christian rap", "rap", "faith based rap", "faith-based rap", "holy hip hop"],
    music_videos: ["music videos", "music video", "a music video"],
    sermons: ["sermons", "a sermon", "sermon", "preaching"],
    choir: ["choir", "choir music", "the choir"],
    movies: ["movies", "a movie", "movie", "a film", "film"],
    videos: ["videos", "a video", "video"],
    lessons: ["lessons", "a lesson", "sunday school", "bible study"],
  };
  const nqEarly = query.replace(/[^a-z0-9\s-]+/g, "").trim();
  for (const b of buckets) {
    if ((GENERIC_QUERIES[b] || []).includes(nqEarly)) {
      const cfg = BUCKET_ROUTES[b];
      try {
        const res = await api.get(cfg.endpoint);
        const items = res?.data?.items || res?.data?.videos || res?.data || [];
        const first = Array.isArray(items) ? items[0] : null;
        if (navigate) navigate(first?.id ? `${cfg.route}?play=${encodeURIComponent(first.id)}` : cfg.route);
      } catch (_) {
        if (navigate) navigate(cfg.route);
      }
      return true;
    }
  }

  // Normalise so "T.D. Jakes" matches "td jakes" and "KJ-52" matches "kj 52".
  const norm = (s) => (s || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const nq = norm(query);
  const qWords = nq.split(/\s+/).filter((w) => w.length >= 2);

  // Score every track in every candidate bucket, then pick the
  // best-ranked across ALL buckets — this lets "play War Room" beat
  // a coincidentally-matching christian-rap track titled "War".
  let best = { score: 0, item: null, bucket: null };
  for (const b of buckets) {
    const cfg = BUCKET_ROUTES[b];
    if (!cfg) continue;
    try {
      const res = await api.get(cfg.endpoint);
      const items = res?.data?.items || res?.data?.videos || res?.data || [];
      for (const it of items) {
        const fields = [it.title, it.artist, it.speaker, it.teacher, it.story, it.topic, it.director, it.voice, it.year]
          .filter(Boolean).map(norm);
        const hay = fields.join(" ");
        let score = 0;
        // Whole-phrase match (strongest)
        if (hay.includes(nq) && nq.length >= 4) score += 8;
        // Individual word matches
        for (const word of qWords) {
          if (hay.includes(word)) score += 1;
          // Bonus when a word starts a field — "lecrae" matches the
          // artist "lecrae ft. mali music"
          for (const f of fields) {
            if (f.startsWith(word + " ") || f === word) { score += 2; break; }
          }
        }
        if (score > best.score) {
          best = { score, item: it, bucket: b };
        }
      }
    } catch (_) {
      // try next bucket
    }
  }

  if (best.item && best.score >= 2) {
    const cfg = BUCKET_ROUTES[best.bucket];
    if (navigate) navigate(`${cfg.route}?play=${encodeURIComponent(best.item.id)}`);
    return true;
  }
  // Nothing matched — surface a search instead so the user isn't dead-ended.
  if (navigate) navigate(`/search?q=${encodeURIComponent(query)}`);
  voiceSpeak("I couldn't find that. Showing search results.");
  return false;
}
